import { BaseCampaignItemPatchDTO } from '@retail/campaign/types';
import { useCampaignTFunction } from '@retail/campaign/i18n';
import { CampaignTenantType, useEditCampaignItems } from '@retail/campaign/data-access';
import { useToast } from '@retail/hooks';
import { useCallback, useMemo } from 'react';
import { useInvalidateCampaignQueries } from './useInvalidateCampaignQueries';

interface Props {
  campaignId: string;
  type: CampaignTenantType;
}

export const useEditCampaignItemsActions = <T extends BaseCampaignItemPatchDTO>({
  campaignId,
  type,
}: Props) => {
  const toast = useToast();
  const t = useCampaignTFunction();
  const invalidateCampaignQueries = useInvalidateCampaignQueries({ campaignId });

  const { mutateAsync: mutateEditCampaignItems, isPending: isLoadingEditToCampaign } =
    useEditCampaignItems({ campaignId, type });

  const handleEditItems = useCallback(
    async (campaignItems: T[]) =>
      await mutateEditCampaignItems({ body: campaignItems })
        .then(() => {
          toast.success(
            campaignItems.length === 1
              ? t('campaign.messages.itemUpdated', { count: campaignItems.length })
              : t('campaign.messages.itemsUpdated', { count: campaignItems.length })
          );
          invalidateCampaignQueries();
        })
        .catch((error) => {
          console.error(error);
          toast.error(t('campaign.error.genericError'));
        }),
    [invalidateCampaignQueries, mutateEditCampaignItems, t, toast]
  );

  return useMemo(
    () => ({
      isLoadingEditToCampaign,
      handleEditItems,
    }),
    [isLoadingEditToCampaign, handleEditItems]
  );
};
